import { Form, Input, Select } from 'antd';
import CurrencyInput from 'components/UI/CurrencyInputNew';
import Monetary from 'components/UI/Data/Monetary';
import * as BuyIntentionModels from 'model/BuyIntention';
import { useTranslation } from 'react-i18next';
import * as S from 'screens/BuyIntention/List/Cards/styles';
import { VerticalFormFields } from 'styles/VerticalFormFields';
import { EditIntentionItemActions } from './useEditIntentionItemActions';

const { Option } = Select;

interface Props {
  item: BuyIntentionModels.BuyIntentionData;
  actions: EditIntentionItemActions;
}

function getPopupContainer(triggerNode: HTMLElement) {
  return triggerNode.parentElement as HTMLElement;
}

export default function EditIntentionItemFields({ item, actions }: Props) {
  const { t } = useTranslation();
  const {
    customer,
    changeOfferInput,
    choicePacking,
    choiceProducer,
    changeQuantityPacking,
    changeQuantityUnit,
    changeUnitPrice,
  } = actions;

  const shippingValue = item.shippingFeeFilials?.productShippingValue ?? 0;

  return (
    <S.SheetFields>
      <Form layout="vertical" component="div">
        <VerticalFormFields>
          <Form.Item label={t('buyIntention.table.producer')}>
            <Select
              dropdownStyle={{ maxHeight: 200, overflow: 'auto' }}
              getPopupContainer={getPopupContainer}
              disabled={item.siteList === null}
              value={
                item.site !== null
                  ? `${item.site?.account} - ${item.site?.tradingName}`
                  : undefined
              }
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption
              placeholder={
                item.siteList === null
                  ? `${item.site?.account} - ${item.site?.tradingName}`
                  : 'Selecione'
              }
              onChange={(v) => choiceProducer(Number(v), item)}
            >
              {Array.isArray(item.siteList) &&
                item.siteList.map((site) => (
                  <Option key={site.id} value={site.id}>
                    {`${site.account} - ${site.tradingName}`}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item label={t('buyIntention.table.quality')}>
            <Select
              getPopupContainer={getPopupContainer}
              disabled={!item.site}
              value={item.qualitySelect || undefined}
              placeholder="Selecione"
              allowClear={false}
              onChange={(v) => changeOfferInput(v, 'qualitySelect', item)}
            >
              {item.qualityList.map((quality) => (
                <Option key={quality.id} value={quality.code}>
                  {quality.description}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label={t('buyIntention.table.packing')}>
            <Select
              getPopupContainer={getPopupContainer}
              disabled={!item.productPreference}
              value={item.packingSelected?.code || undefined}
              placeholder="Selecione"
              allowClear={false}
              showSearch
              optionFilterProp="children"
              filterOption
              onChange={(v: string) => choicePacking(v, item)}
            >
              {item.productPreference &&
                item.productPreference.map((packing) => (
                  <Option value={packing.code} key={packing.id}>
                    {packing.code} - {packing.abbrev}
                  </Option>
                ))}
            </Select>
            {item.packingSelected?.additionalCost === 'S' && (
              <S.FieldHint>
                <Monetary symbol value={item.packingSelected.packingCost} />
                /un.
              </S.FieldHint>
            )}
          </Form.Item>

          <Form.Item label={t('buyIntention.table.qe')}>
            <S.QuantityInput
              aria-label={t('buyIntention.table.qe')}
              disabled={!item.packingSelected}
              inputMode="numeric"
              value={item.quantityPacking}
              min={1}
              step={item.packingSelected?.qe || 1}
              onChange={(value) => changeQuantityPacking(Number(value), item)}
            />
            {item.packingSelected && (
              <S.FieldHint>
                {`Múltiplo ${item.packingSelected?.qe || ''}`}
              </S.FieldHint>
            )}
          </Form.Item>

          <Form.Item label={t('buyIntention.table.qpe')}>
            <S.QuantityInput
              aria-label={t('buyIntention.table.qpe')}
              disabled={!item.packingSelected}
              inputMode="numeric"
              value={item.quantityUnit}
              min={1}
              step={1}
              onChange={(value) => changeQuantityUnit(Number(value), item)}
            />
          </Form.Item>

          <Form.Item
            label={`${t('buyIntention.table.unitValue')} ${t(
              'buyIntention.table.noICMS',
            )}`}
          >
            <div className="currency-field">
              <CurrencyInput
                disabledOption={!item.packingSelected}
                id={`edit-sheet-${item.id}`}
                inputMode="decimal"
                value={parseFloat(String(item.unitPrice))}
                onChange={(value) => changeUnitPrice(value, item)}
              />
            </div>
          </Form.Item>

          {customer?.branch === 'CVH_UB' && (
            <Form.Item label={t('buyIntention.table.shippingFee')}>
              <Monetary symbol value={shippingValue} />
              <S.FieldHint>
                {t('buyIntention.table.shippingTotalFee')}:{' '}
                <Monetary
                  symbol
                  value={
                    item.quantityPacking * item.quantityUnit * shippingValue
                  }
                />
              </S.FieldHint>
            </Form.Item>
          )}

          <Form.Item label={t('buyIntention.table.observation')}>
            <Input
              aria-label={t('buyIntention.table.observation')}
              value={item.observationText}
              onChange={(e) =>
                changeOfferInput(e.target.value, 'observationText', item)
              }
            />
          </Form.Item>
        </VerticalFormFields>
      </Form>

      <S.TotalRow>
        <span>{t('buyIntention.table.totalValue')}</span>
        <span>
          <Monetary symbol value={item.totalPrice} />
        </span>
      </S.TotalRow>
    </S.SheetFields>
  );
}
